import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { DocumentsListViewModel } from '@/features/docs/DocumentsListViewModel'
import type { DocumentLinkingViewModel } from '@/features/docs/components/LinkDocumentsDialog/DocumentLinkingViewModel'
import type {
  DocumentsListViewModelFactory,
  DocumentLinkingViewModelFactory,
} from '@/features/docs'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'

/**
 * View model for the job's documents.
 */
export class JobDocumentsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  public readonly documentsListVm: DocumentsListViewModel
  public readonly linkDocumentsVm: DocumentLinkingViewModel

  constructor(
    createDocumentsListVm: DocumentsListViewModelFactory,
    createLinkDocumentsVm: DocumentLinkingViewModelFactory,
    job: SubmittedJob,
  ) {
    this.documentsListVm = createDocumentsListVm({
      fetchAuthor: true,
      context: {
        type: 'Job',
        jobId: job.id,
      },
    })

    this.linkDocumentsVm = createLinkDocumentsVm({
      filter: (doc) => !doc.jobIds.includes(job.id),
      context: {
        type: 'Job',
        jobId: job.id,
      },
      onLinkingCompleted: (doc) => {
        this.documentsListVm.addDocument(doc)
        return Promise.resolve()
      },
    })

    // Manage the view models with the lifecycle
    this._lifecycle.manage(this.documentsListVm)
    this._lifecycle.manage(this.linkDocumentsVm)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetchDocuments(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the documents.
   *
   * @param signal
   */
  fetchDocuments(signal?: AbortSignal) {
    return this.documentsListVm.fetch(signal)
  }
}
